import type { ConnectionAccessRole, PmsConnectionSummary, PmsCredentials, SyncFrequencyMinutes, TenantMember, ViewerKind } from "../types/config";
import type { CredentialFieldType, PmsProviderType } from "./pmsApi";
import type { RatePlansResponse, RoomTypeOption } from "./pricingApi";
import { apiFetch } from "./apiClient";
import { readViewerKind, viewerKindOf } from "./viewerKind";
import {
  connectionIdsQuery,
  readConnectionIds,
  readConnectionMembers,
  readMemberRow,
  readRefusalConnectionIds,
  readRemovedAccess,
  withConnectionIds,
  type ConnectionMembers,
  type RemovedAccess,
} from "./memberScope";

export async function fetchPmsConnections(): Promise<PmsConnectionSummary[]> {
  const res = await apiFetch("/api/pms-connections");
  if (!res.ok) throw new Error(`Failed to fetch connections: ${res.status}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Failed to fetch connections");
  // A viewer's card carries the kind of their grant, read so that a kind this
  // page does not know narrows rather than widens.
  return (data.connections as PmsConnectionSummary[]).map((conn) => ({
    ...conn,
    viewerKind: viewerKindOf(conn.role, conn.viewerKind),
  }));
}

export interface CredentialFieldPreview {
  name: string;
  type: CredentialFieldType;
  value: string;
}

export interface PmsConnectionDetails {
  id: number;
  name: string | null;
  currency: string;
  providerType: PmsProviderType;
  credentialFields: CredentialFieldPreview[];
  syncFrequencyMinutes: SyncFrequencyMinutes;
  lastSyncAt: string | null;
}

export async function fetchPmsConnectionCredentials(
  id: number,
): Promise<PmsConnectionDetails> {
  const res = await apiFetch(`/api/pms-connections/${id}`);
  if (!res.ok) throw new Error(`Failed to fetch connection: ${res.status}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Failed to fetch connection");
  return data.connection;
}

export async function createPmsConnection(
  creds: PmsCredentials,
  name?: string,
  currency?: string,
  providerType?: PmsProviderType,
): Promise<PmsConnectionSummary> {
  const res = await apiFetch("/api/pms-connections", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, credentials: creds, currency, providerType }),
  });
  if (!res.ok) throw new Error(`Failed to create connection: ${res.status}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Failed to create connection");
  return data.connection;
}

export async function updatePmsConnection(
  id: number,
  updates: { name?: string; credentials?: Partial<PmsCredentials> },
): Promise<void> {
  const res = await apiFetch(`/api/pms-connections/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error(`Failed to update connection: ${res.status}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Failed to update connection");
}

export async function fetchConnectionSyncFrequency(
  id: number,
): Promise<{ id: number; syncFrequencyMinutes: SyncFrequencyMinutes; lastSyncAt: string | null }> {
  const res = await apiFetch(`/api/pms-connections/${id}/sync-frequency`);
  if (!res.ok) throw new Error(`Failed to fetch sync frequency: ${res.status}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Failed to fetch sync frequency");
  return data.connection;
}

// There is no operator-facing writer for the cadence. How often Optivo polls
// somebody else's PMS is an operational decision support makes per account,
// so the only way in is `updateAdminSyncFrequency` in `adminConnectionsApi`.
// The reader above stays: the auto-sync dialog and the sidebar's sync
// indicator both state the cadence without offering to change it.

/**
 * The room types of one connection — the property a screen configures, which
 * need not be the one the user has active. `fetchRoomTypes` in `pricingApi`
 * answers for the active connection: right for a screen working on that
 * property, wrong for the auto-sync dialog, which the Connections page mounts
 * once per property card and which saves what it offers onto that card's
 * schedule.
 */
export async function fetchConnectionRoomTypes(id: number): Promise<RoomTypeOption[]> {
  const res = await apiFetch(`/api/pms-connections/${id}/room-types`);
  if (!res.ok) throw new Error(`Failed to fetch room types: ${res.status}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Failed to fetch room types");
  return data.roomTypes;
}

/** The rate plans of one connection, with its own base plan — see {@link fetchConnectionRoomTypes}. */
export async function fetchConnectionRatePlans(id: number): Promise<RatePlansResponse> {
  const res = await apiFetch(`/api/pms-connections/${id}/rate-plans`);
  if (!res.ok) throw new Error(`Failed to fetch rate plans: ${res.status}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Failed to fetch rate plans");
  return { plans: data.plans, basePlan: data.basePlan };
}

export async function deletePmsConnection(id: number): Promise<void> {
  const res = await apiFetch(`/api/pms-connections/${id}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`Failed to delete connection: ${res.status}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Failed to delete connection");
}

export async function activatePmsConnection(id: number): Promise<void> {
  const res = await apiFetch(`/api/pms-connections/${id}/activate`, { method: "POST" });
  if (!res.ok) throw new Error(`Failed to activate connection: ${res.status}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Failed to activate connection");
}

export async function testPmsConnectionHealth(id: number): Promise<{ healthy: boolean; error?: string }> {
  const res = await apiFetch(`/api/pms-connections/${id}/health`);
  if (!res.ok) throw new Error(`Failed to check health: ${res.status}`);
  const data = await res.json();
  return { healthy: data.status === "healthy", error: data.error };
}

/**
 * The people who reach a property — its owners, the members whose access
 * covers it and its read-only grants — with which of the account's
 * properties each reaches, and the account's properties, where the server
 * tells them: to an owner of the account. An older server, or a member who
 * does not own it, is told neither, and the panel then asks nothing about
 * which properties.
 */
export async function fetchConnectionMembers(id: number): Promise<ConnectionMembers> {
  const res = await apiFetch(`/api/pms-connections/${id}/members`);
  if (!res.ok) throw new Error(`Failed to fetch members: ${res.status}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Failed to fetch members");
  return readConnectionMembers(data);
}

/**
 * A refusal from the access routes, with the server's `code` where it sent
 * one — `ALREADY_MEMBER` for a read-only invite of somebody whose full access
 * already covers a property it names, `ALREADY_READ_ONLY` for an invite of
 * somebody who already reads one read-only, in which case `viewerKind` is the
 * kind they hold — and `connectionIds`, the properties the refusal is about,
 * which the panel words for itself.
 */
export class ConnectionMemberError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    readonly viewerKind?: ViewerKind,
    readonly connectionIds?: number[],
  ) {
    super(message);
    this.name = "ConnectionMemberError";
  }
}

/** Read one of the access routes' refusals, with the kind somebody holds where it names one. */
async function memberError(res: Response, fallback: string): Promise<ConnectionMemberError> {
  const data = await res.json().catch(() => ({}));
  const code = typeof data.code === "string" ? data.code : undefined;
  return new ConnectionMemberError(
    data.error || `${fallback}: ${res.status}`,
    code,
    // Read so that a kind this page does not know narrows rather than widens.
    code === "ALREADY_READ_ONLY" ? readViewerKind(data.viewerKind) : undefined,
    readRefusalConnectionIds(data.connectionIds),
  );
}

/** The person a path names, encoded: a Logto id can carry characters a path cannot. */
function memberPath(id: number, memberId: string): string {
  return `/api/pms-connections/${id}/members/${encodeURIComponent(memberId)}`;
}

/**
 * Invite somebody by e-mail or user id. `member` (the default) gives full
 * access; `viewer` read-only access of the kind named — `management` for the
 * prices and analytics, `front_desk` for the prices and reservations. The
 * kind is sent for a read-only invite alone.
 *
 * `connectionIds` names the properties of the account the access covers:
 * null for the whole account, standing (full access alone), or the ids. Left
 * out, the body is what a page from before scopes sent, which the server
 * reads as the whole account for full access and this property for a
 * read-only grant.
 *
 * Answers with the person as the server stored them, the kind of a viewer's
 * grant read through `viewerKindOf` and their reach through `memberScope`: a
 * server from before the kinds ignores the kind and states none, which reads
 * as the management grant it gave, and one from before scopes states no
 * reach — so a caller checks the answer, never its own request. Null where
 * the answer names nobody.
 */
export async function addConnectionMember(
  id: number,
  identifier: string,
  role: "member" | "viewer" = "member",
  viewerKind?: ViewerKind,
  connectionIds?: readonly number[] | null,
): Promise<TenantMember | null> {
  const body = role === "viewer" && viewerKind ? { identifier, role, viewerKind } : { identifier, role };
  const res = await apiFetch(`/api/pms-connections/${id}/members`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(withConnectionIds(body, connectionIds)),
  });
  if (!res.ok) throw await memberError(res, "Failed to add member");
  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Failed to add member");
  const member = data.member as TenantMember | undefined;
  if (!member || typeof member !== "object") return null;
  return readMemberRow(member);
}

/**
 * Change what a person may do with this property: owner or member, or
 * read-only on it (`viewer`) of the kind named. A change made here changes
 * this property: a member made read-only keeps full access to the other
 * properties their access covered. The kind is sent for `viewer` alone, and
 * an existing grant keeps its kind when none is named. Owner-only; the server
 * refuses to take away a tenant's last owner, whichever role it is to.
 *
 * `connectionIds` names, for a read-only person given full access, the
 * properties it covers (this one always among them on the server), or null
 * for the whole account; it is sent with a tenant role alone. Left out, the
 * server reads the change as a page from before scopes meant it.
 *
 * Answers with the kind the grant holds now, as the server stated it, and
 * none where it stated none — which a server from before the kinds never
 * does, so a caller that asked for the front desk and got no kind back did
 * not get it — and, after a move to a tenant role, the scope the membership
 * reaches now where the server stated one.
 */
export async function setConnectionMemberRole(
  id: number,
  memberId: string,
  role: ConnectionAccessRole,
  viewerKind?: ViewerKind,
  connectionIds?: readonly number[] | null,
): Promise<{ viewerKind?: ViewerKind; connectionIds?: number[] | null }> {
  const body = role === "viewer" ? (viewerKind ? { role, viewerKind } : { role }) : withConnectionIds({ role }, connectionIds);
  const res = await apiFetch(memberPath(id, memberId), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await memberError(res, "Failed to change member role");
  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Failed to change member role");
  const scope = readConnectionIds(data.connectionIds);
  return {
    viewerKind: data.viewerKind === undefined || data.viewerKind === null ? undefined : readViewerKind(data.viewerKind),
    ...(scope !== undefined ? { connectionIds: scope } : {}),
  };
}

/**
 * Take a person's access away. With `connectionIds`, from exactly those
 * properties of the account: their membership stops reaching them, and their
 * read-only grants on them go. Without, as a page from before scopes asked:
 * the read-only grant on this property, else the membership from the whole
 * account. A refusal carries its code (`OWNER` for an owner, who is demoted
 * first).
 */
export async function removeConnectionMember(
  id: number,
  memberId: string,
  connectionIds?: readonly number[],
): Promise<RemovedAccess> {
  const query = connectionIds ? connectionIdsQuery(connectionIds) : "";
  const res = await apiFetch(`${memberPath(id, memberId)}${query}`, { method: "DELETE" });
  if (!res.ok) throw await memberError(res, "Failed to remove member");
  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Failed to remove member");
  return readRemovedAccess(data);
}

/**
 * Whether an e-mail or Logto user id belongs to an account. The server answers
 * nothing else — the lookup is open to every signed-in user, so it never names
 * the account it found.
 */
export async function lookupUser(identifier: string): Promise<{ found: boolean }> {
  const res = await apiFetch("/api/pms-connections/lookup-user", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ identifier }),
  });
  if (!res.ok) throw new Error(`Lookup failed: ${res.status}`);
  const data = await res.json();
  return { found: !!data.found };
}
